import { NgFor, NgClass, NgIf } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';

import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatFooterCellDef,
  MatFooterCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatFooterRowDef,
  MatFooterRow,
} from '@angular/material/table';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  FormDialogComponent,
} from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { ProductTypeEnum } from 'src/app/delivery/models/delivery.model';

import {
  BarnDistributionFormInputData,
  VisualInformationInput,
  BarnDistributionFormResultData,
  VisualInformationBarnBalanceRow,
  BalanceTableRow,
  Barn,
} from '../../models/cycle.model';

@Component({
  selector: 'app-barn-distribution-form',
  templateUrl: './barn-distribution-form.component.html',
  styleUrls: ['./barn-distribution-form.component.scss'],
  imports: [
    FormDialogComponent,
    ReactiveFormsModule,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    NgFor,
    MatFormField,
    MatLabel,
    MatInput,
    MatFooterCellDef,
    MatFooterCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NgClass,
    MatFooterRowDef,
    MatFooterRow,
    NgIf,
    MatIcon,
    TranslatePipe,
    AgrNumberPipe,
  ],
})
export class BarnDistributionFormComponent
  extends AbstractFormDialog<BarnDistributionFormResultData>
  implements OnInit
{
  protected mdDialogRef =
    inject<
      MatDialogRef<
        BarnDistributionFormComponent,
        AbstractDialogResultData<BarnDistributionFormResultData>
      >
    >(MatDialogRef);
  data = inject<BarnDistributionFormInputData>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  private readonly locale = inject(LocaleService);

  public informationData: VisualInformationInput = null;
  public currentlyAllocated = -1;
  consumptionWithinCycle: number[] = [];
  displayedColumns = ['productTitle', 'consumptionWithinCycle'];
  displayedTotalColumns: string[] = [];
  displayedTotalRawColumns: string[] = [];
  displayedTotalCompoundColumns: string[] = [];
  barns: Barn[] = [];
  datasource: MatTableDataSource<BalanceTableRow>;
  formGroup = this.fb.group({});

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    super();
  }
  ngOnInit(): void {
    if (!!this.data.balanceTableRows && this.data.balanceTableRows.length > 0) {
      const consumptionInOtherCycles: number[] = [];
      const availableInThisCycle: number[] = [];
      const currentRemainder: number[] = [];
      let balanceRows: VisualInformationBarnBalanceRow[] = [];
      let balanceRow: VisualInformationBarnBalanceRow;
      let toAllocate: number;
      let totalChicks: number;
      let totalConsumption: number;
      let rowBarn: Barn;

      this.data.balanceTableRows.forEach((row, index) => {
        consumptionInOtherCycles.push(
          row.balanceInOtherCycles.reduce(
            (total, barnBalanceRow) => total + barnBalanceRow.amount,
            0,
          ),
        );
        availableInThisCycle.push(row.quantity);
        this.consumptionWithinCycle.push(availableInThisCycle[index] - row.remainder);
        currentRemainder.push(row.remainder);
        totalConsumption = 0;

        if (row.barnId) {
          rowBarn = row.barnBalanceRows.find((b) => b.barn.id === row.barnId).barn;
          toAllocate = this.consumptionWithinCycle[index];

          balanceRow = {
            productType: row.productType,
            balanceRowId: row.balanceRowId,
            barn: rowBarn,
            amount: toAllocate,
            ratio: 1,
            totalChicks,
            kgPerChick: parseFloat((toAllocate / totalChicks).toFixed(3)),
          };

          return balanceRows.push(balanceRow);
        } else {
          balanceRows = [
            ...balanceRows,
            ...row.barnBalanceRows.map((b) => {
              if (b.barn.id !== row.barnBalanceRows[row.barnBalanceRows.length - 1].barn.id) {
                toAllocate = Math.round(b.ratio * this.consumptionWithinCycle[index]);
                totalConsumption += toAllocate;
              } else {
                toAllocate = this.consumptionWithinCycle[index] - totalConsumption;
              }

              totalChicks = this.data.cycle.dayOldChicks
                .filter((d) => d.barn?.id === b.barn?.id)
                .reduce((total, dayOldChick) => total + dayOldChick.amountOfChicks, 0);

              return {
                productType: row.productType,
                balanceRowId: row.balanceRowId,
                barn: b.barn,
                amount: toAllocate,
                ratio: b.ratio,
                totalChicks,
                kgPerChick: parseFloat((toAllocate / totalChicks).toFixed(3)),
              } as VisualInformationBarnBalanceRow;
            }),
          ];
        }
      });

      this.informationData = {
        consumptionInOtherCycles: consumptionInOtherCycles.reduce((sum, amount) => sum + amount, 0),
        availableInThisCycle: availableInThisCycle.reduce((sum, amount) => sum + amount, 0),
        currentRemainder: currentRemainder.reduce((sum, amount) => sum + amount, 0),
        consumptionWithinCycle: this.consumptionWithinCycle.reduce(
          (sum, amount) => sum + amount,
          0,
        ),
        balanceRows,
      };

      this.datasource = new MatTableDataSource(
        this.data.balanceTableRows.map((b) => Object.assign({}, b)),
      );

      const barnIds = Array.from(new Set(balanceRows.map((br) => br.barn?.id)));
      barnIds.forEach((barnId) =>
        this.barns.push(balanceRows.map((br) => br.barn).find((b) => b.id === barnId)),
      );
      this.displayedColumns.push(...this.barns.map((b) => b.name));

      this.displayedColumns.forEach((dc) => {
        this.displayedTotalColumns.push('total_' + dc);
        this.displayedTotalRawColumns.push('totalRaw_' + dc);
        this.displayedTotalCompoundColumns.push('totalCompound_' + dc);
      });

      this.currentlyAllocated = this.informationData.balanceRows.reduce(
        (total, balanceRow) => total + balanceRow.amount,
        0,
      );
    }
  }

  barnAmountChanged(row: VisualInformationBarnBalanceRow, barn: Barn, event: Event): void {
    row.amount = +(event.target as HTMLInputElement).value;
    row.kgPerChick = parseFloat((row.amount / row.totalChicks).toFixed(3));

    this.informationData.balanceRows.forEach((br) => {
      if (
        br.balanceRowId === row.balanceRowId &&
        br.barn?.id === barn.id &&
        !this.disableBarnsEditing(br, barn)
      ) {
        br.amount = +(event.target as HTMLInputElement).value;
      }
    });

    this.currentlyAllocated = this.informationData.balanceRows.reduce(
      (total, barnBalanceRow) => total + barnBalanceRow.amount,
      0,
    );
  }

  disableBarnsEditing(row: VisualInformationBarnBalanceRow, barn: Barn): boolean {
    return (
      this.informationData.balanceRows.find(
        (br) => br.balanceRowId === row.balanceRowId && br.barn?.id === barn.id,
      ) === undefined
    );
  }

  getConsumptionWithinCycle(_row: VisualInformationBarnBalanceRow, index: number) {
    return this.consumptionWithinCycle[index];
  }

  getBarnQuantity(row: VisualInformationBarnBalanceRow, barn: Barn) {
    return (
      this.informationData.balanceRows.find(
        (br) => br.balanceRowId === row.balanceRowId && br.barn.id === barn.id,
      )?.amount || 0
    );
  }

  getFooterValue(columnName: string, index: number) {
    if (columnName.includes('total_')) {
      // productTitle
      if (index === 0) {
        return this.locale.translate('balance.barn-distribution.total');
      }
      // quantity
      if (index === 1) {
        return this.informationData.balanceRows.reduce(
          (total, barnBalanceRow) => total + barnBalanceRow.amount,
          0,
        );
      }
      // barns
      if (index > 1) {
        return this.informationData.balanceRows
          .filter((br) => columnName.includes(br.barn.name))
          .reduce((total, barnBalanceRow) => total + barnBalanceRow.amount, 0);
      }
    }

    if (columnName.includes('totalRaw_')) {
      // productTitle
      if (index === 0) {
        return this.locale.translate('balance.barn-distribution.totalRaw');
      }
      // quantity
      if (index === 1) {
        return this.informationData.balanceRows
          .filter((br) => br.productType === ProductTypeEnum.RAWPRODUCT)
          .reduce((total, barnBalanceRow) => total + barnBalanceRow.amount, 0);
      }
      // barns
      if (index > 1) {
        return this.informationData.balanceRows
          .filter(
            (br) =>
              columnName.includes(br.barn.name) && br.productType === ProductTypeEnum.RAWPRODUCT,
          )
          .reduce((total, barnBalanceRow) => total + barnBalanceRow.amount, 0);
      }
    }

    if (columnName.includes('totalCompound_')) {
      // productTitle
      if (index === 0) {
        return this.locale.translate('balance.barn-distribution.totalCompound');
      }
      // quantity
      if (index === 1) {
        return this.informationData.balanceRows
          .filter((br) => br.productType === ProductTypeEnum.MIXEDPRODUCT)
          .reduce((total, barnBalanceRow) => total + barnBalanceRow.amount, 0);
      }
      // barns
      if (index > 1) {
        return this.informationData.balanceRows
          .filter(
            (br) =>
              columnName.includes(br.barn.name) && br.productType === ProductTypeEnum.MIXEDPRODUCT,
          )
          .reduce((total, barnBalanceRow) => total + barnBalanceRow.amount, 0);
      }
    }
  }

  confirm(): void {
    if (this.currentlyAllocated === this.informationData.consumptionWithinCycle) {
      const updatedBalanceTableRows = [...this.datasource.data];
      this.informationData.balanceRows.forEach((balanceRow) => {
        updatedBalanceTableRows
          .filter((ubtr) => ubtr.balanceRowId === balanceRow.balanceRowId)
          .forEach((row) => {
            row.barnBalanceRows = [
              ...row.barnBalanceRows.filter((r) => r.barn.id !== balanceRow.barn.id),
              {
                ...row.barnBalanceRows.find((r) => r.barn.id === balanceRow.barn.id),
                amount: balanceRow.amount,
                ratio: balanceRow.amount
                  ? balanceRow.amount /
                    this.informationData.balanceRows
                      .filter((bbr) => bbr.balanceRowId === row.balanceRowId)
                      .reduce((total, barnBalanceRow) => total + barnBalanceRow.amount, 0)
                  : 0,
              },
            ];
          });
      });

      if (this.data?.cycle?.isFinalBalance) {
        super.cancel();
      } else {
        super.confirm({
          balanceTableRows: updatedBalanceTableRows,
        });
      }
    }
  }
}
